/**
 * Problem Slide
 * Side-by-side layout: interactive diagram (left) | alert boxes (right)
 * Clicking an entity or company opens a detail dialog.
 *
 * Diagram text uses <foreignObject> for reliable CSS-based text layout
 * inside SVG shapes (flexbox centering, natural wrapping, overflow control).
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { SlideBase, SlideHeader, SlideNote, VerticalDivider, SlideAlertPanel, SlideAlertBox, DarkInsightEmphasis } from '@components/press/pitch-deck/slide-primitives';


// ============================================
// Side-by-side body
// ============================================

const BodyRow = styled.div`
  width: 100%;
  max-height: 70%;
  display: flex;
  gap: 0;
  align-items: stretch;
`;

const DiagramPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

/** Alert panel sized as a flex peer of DiagramPanel inside BodyRow. */
const RowAlertPanel = styled(SlideAlertPanel)`
  flex: 1;
  min-width: 0;
`;

// ============================================
// Detail Dialog
// ============================================

const DialogOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(30, 41, 59, 0.4);
  backdrop-filter: blur(4px);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DialogCard = styled(motion.div)`
  background: ${colors.white};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl} ${spacing['2xl']};
  max-width: 440px;
  width: 90%;
  box-shadow: ${colors.shadows.modal};
  position: relative;
`;

const DialogClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: ${colors.neutral.ultraLight};
  color: ${colors.highlight.light};
  font-size: ${typography.fontSize.base};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${colors.neutral.extraLight};
    color: ${colors.highlight.dark};
  }
`;

const DialogTitle = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
  margin: 0 0 ${spacing.sm};
`;

const DialogSubtitle = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.xs};
  color: ${colors.highlight.light};
  margin-bottom: ${spacing.md};
`;

const DialogStat = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${spacing.xs} 0;
  border-bottom: 1px solid ${colors.neutral.extraLight};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};

  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.span`
  color: ${colors.highlight.light};
`;

const StatValue = styled.span`
  color: ${colors.highlight.dark};
  font-weight: ${typography.fontWeight.semibold};
`;

// ============================================
// SVG Constants
// ============================================

const FONT = "'Poppins', 'SF Pro Display', sans-serif";

const C = {
  momFill: '#FEEDED',
  momStroke: '#DF6565',
  momText: '#DF6565',
  entityFill: '#D8F3ED',
  entityStroke: '#B8E6DB',
  entityText: '#334155',
  entitySub: '#64748B',
  solidLine: '#B8E6DB',
  dashedLine: '#CBD5E1',
  pillFill: '#F1F5F9',
  pillStroke: '#E2E8F0',
  pillText: '#64748B',
} as const;

// ============================================
// Data
// ============================================

interface Entity {
  id: string;
  label: string;
  sub: string;
  cx: number;
  cy: number;
  r: number;
  detail: { subtitle: string; stats: { label: string; value: string }[] };
}

interface Company {
  label: string;
  cx: number;
  cy: number;
  detail: { subtitle: string; stats: { label: string; value: string }[] };
}

const MOM = { cx: 450, cy: 235, r: 50 };

const ENTITIES: Entity[] = [
  {
    id: 'kids',
    label: 'Kids',
    sub: 'School · Health · Activities',
    cx: 450,
    cy: 72,
    r: 62,
    detail: {
      subtitle: 'Rising costs and complexity of raising children',
      stats: [
        { label: 'Avg childcare cost', value: '$16,000/yr' },
        { label: 'Activities per child', value: '2.3 avg' },
        { label: 'School choice options (urban)', value: '15-50+' },
        { label: 'Medical visits/yr (under 5)', value: '7-10' },
        { label: 'US families with kids', value: '33.3M' },
      ],
    },
  },
  {
    id: 'partner',
    label: 'Partner',
    sub: 'Dates · Communication',
    cx: 625,
    cy: 148,
    r: 62,
    detail: {
      subtitle: 'Relationship maintenance in busy households',
      stats: [
        { label: 'Couples wanting more date nights', value: '72%' },
        { label: 'Avg date night cost', value: '$100-150' },
        { label: 'Scheduling conflicts as cause of stress', value: '#2' },
        { label: 'Married couples with kids', value: '24.2M' },
      ],
    },
  },
  {
    id: 'home',
    label: 'Home',
    sub: 'Repairs · Bills · Security',
    cx: 570,
    cy: 360,
    r: 62,
    detail: {
      subtitle: 'Home maintenance burden and fragmentation',
      stats: [
        { label: 'Avg annual maintenance', value: '$4,800/yr' },
        { label: 'Home insurance (avg premium)', value: '$2,300/yr' },
        { label: 'Service providers per household', value: '8-12' },
        { label: 'US homeowner households', value: '86M' },
      ],
    },
  },
  {
    id: 'pets',
    label: 'Pets',
    sub: 'Vet · Food · Grooming',
    cx: 330,
    cy: 360,
    r: 62,
    detail: {
      subtitle: 'Pet care costs rising faster than inflation',
      stats: [
        { label: 'Avg annual pet cost (dog)', value: '$1,500-2,000' },
        { label: 'Vet costs (10yr increase)', value: '+60%' },
        { label: 'Pet insurance market CAGR', value: '16%' },
        { label: 'US pet-owning households', value: '67M' },
      ],
    },
  },
  {
    id: 'auto',
    label: 'Auto',
    sub: 'Service · Insurance · Safety',
    cx: 275,
    cy: 148,
    r: 62,
    detail: {
      subtitle: 'Vehicle ownership complexity and cost',
      stats: [
        { label: 'Avg annual auto insurance', value: '$2,300/yr' },
        { label: 'Avg maintenance cost', value: '$1,200/yr' },
        { label: 'Open safety recalls (US)', value: '67M vehicles' },
        { label: 'Multi-car households', value: '57%' },
      ],
    },
  },
];

const COMPANIES: Record<string, Company[]> = {
  kids: [
    {
      label: 'Care.com',
      cx: 280,
      cy: 14,
      detail: { subtitle: 'Caregiving marketplace', stats: [{ label: 'Acquired by IAC', value: '$500M' }, { label: 'Families served', value: '35M+' }] },
    },
    {
      label: 'ClassDojo',
      cx: 525,
      cy: -15,
      detail: { subtitle: 'School communication', stats: [{ label: 'Valuation', value: '~$1.5B' }, { label: 'Students', value: '51M' }] },
    },
    {
      label: 'Niche',
      cx: 650,
      cy: 24,
      detail: { subtitle: 'School rankings & reviews', stats: [{ label: 'Monthly visitors', value: '50M+' }, { label: 'Schools rated', value: '140K+' }] },
    },
    {
      label: 'Khan Academy',
      cx: 380,
      cy: -20,
      detail: { subtitle: 'AI-powered tutoring', stats: [{ label: 'Learners', value: '150M+' }, { label: 'Khanmigo AI tutor', value: 'GPT-4 powered' }] },
    },
  ],
  partner: [
    {
      label: 'OpenTable',
      cx: 748,
      cy: 60,
      detail: { subtitle: 'Restaurant reservations', stats: [{ label: 'Parent (Booking)', value: '$130B mkt cap' }, { label: 'Restaurants', value: '60K+' }] },
    },
    {
      label: 'Yelp',
      cx: 760,
      cy: 135,
      detail: { subtitle: 'Local business reviews', stats: [{ label: 'Market cap', value: '~$2.5B' }, { label: 'Reviews', value: '265M+' }] },
    },
    {
      label: 'UrbanSitter',
      cx: 755,
      cy: 220,
      detail: { subtitle: 'Trusted sitter marketplace', stats: [{ label: 'Total funding', value: '$45M' }, { label: 'Coverage', value: '50 states' }, { label: 'Community groups', value: '300K+' }] },
    },
  ],
  home: [
    {
      label: 'Thumbtack',
      cx: 730,
      cy: 290,
      detail: { subtitle: 'Home services marketplace', stats: [{ label: 'Valuation', value: '~$3.2B' }, { label: 'Professionals', value: '300K+' }] },
    },
    {
      label: 'Ring',
      cx: 755,
      cy: 345,
      detail: { subtitle: 'Home security', stats: [{ label: 'Acquired by Amazon', value: '$1.2B' }, { label: 'Devices sold', value: '10M+' }] },
    },
    {
      label: 'Cozi',
      cx: 750,
      cy: 400,
      detail: { subtitle: 'Family calendar & organizer', stats: [{ label: 'Families using', value: '25M+' }, { label: 'Acquired by', value: 'Time Inc.' }] },
    },
    {
      label: 'Zillow',
      cx: 500,
      cy: 460,
      detail: { subtitle: 'Real estate marketplace', stats: [{ label: 'Market cap', value: '~$14B' }, { label: 'Monthly visitors', value: '200M+' }] },
    },
    {
      label: 'Instacart',
      cx: 650,
      cy: 460,
      detail: { subtitle: 'Grocery delivery', stats: [{ label: 'Market cap', value: '~$10B' }, { label: 'Orders/yr', value: '260M+' }] },
    },
  ],
  pets: [
    {
      label: 'Rover',
      cx: 255,
      cy: 465,
      detail: { subtitle: 'Pet sitting & walking', stats: [{ label: 'Valuation (at IPO)', value: '~$2B' }, { label: 'Sitters', value: '500K+' }] },
    },
    {
      label: 'Chewy',
      cx: 150,
      cy: 445,
      detail: { subtitle: 'Pet food & supplies', stats: [{ label: 'Market cap', value: '~$10B' }, { label: 'Revenue', value: '$11B/yr' }] },
    },
    {
      label: 'Lemonade Pet',
      cx: 160,
      cy: 360,
      detail: { subtitle: 'Pet health insurance', stats: [{ label: 'Lemonade mkt cap', value: '~$1.5B' }, { label: 'Pet insurance CAGR', value: '16%' }] },
    },
  ],
  auto: [
    {
      label: 'CarFax',
      cx: 145,
      cy: 80,
      detail: { subtitle: 'Vehicle history reports', stats: [{ label: 'Revenue', value: '~$1.5B' }, { label: 'Reports run', value: '100M+/yr' }] },
    },
    {
      label: 'AAA',
      cx: 130,
      cy: 135,
      detail: { subtitle: 'Roadside & insurance', stats: [{ label: 'Members', value: '63M' }, { label: 'Revenue', value: '~$16B' }] },
    },
    {
      label: 'Geico',
      cx: 128,
      cy: 190,
      detail: { subtitle: 'Auto insurance', stats: [{ label: 'Parent (Berkshire)', value: '$1T+ mkt cap' }, { label: 'Premiums', value: '$42B/yr' }] },
    },
  ],
};

const PILL_W = 100;
const PILL_H = 36;
const PILL_RX = 18;

// ============================================
// foreignObject text style helpers
// ============================================

/** Column-centered container for multi-line text (entity bubbles) */
const foCenter: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  overflow: 'hidden',
};

/** Single-line centered container (pills, Mom circle) */
const foCenterRow: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  overflow: 'hidden',
};

// ============================================
// Interactive Diagram
// ============================================

interface DiagramProps {
  onEntityClick: (entity: Entity) => void;
  onCompanyClick: (co: Company) => void;
}

const ProblemDiagram: React.FC<DiagramProps> = ({ onEntityClick, onCompanyClick }) => (
  <svg
    viewBox="0 -40 900 530"
    style={{ width: '100%', flex: '1 1 auto', minHeight: 0 }}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Dashed lines: entity → companies */}
    {ENTITIES.map((entity) =>
      (COMPANIES[entity.id] || []).map((co, i) => (
        <line
          key={`${entity.id}-co-${i}`}
          x1={entity.cx}
          y1={entity.cy}
          x2={co.cx}
          y2={co.cy}
          stroke={C.dashedLine}
          strokeWidth={1}
          strokeDasharray="4 3"
        />
      )),
    )}

    {/* Solid lines: Mom → entities */}
    {ENTITIES.map((entity) => (
      <line
        key={`mom-${entity.id}`}
        x1={MOM.cx}
        y1={MOM.cy}
        x2={entity.cx}
        y2={entity.cy}
        stroke={C.solidLine}
        strokeWidth={2.5}
      />
    ))}

    {/* Company pills */}
    {ENTITIES.map((entity) =>
      (COMPANIES[entity.id] || []).map((co, i) => (
        <g
          key={`pill-${entity.id}-${i}`}
          style={{ cursor: 'pointer' }}
          onClick={() => onCompanyClick(co)}
        >
          <rect
            x={co.cx - PILL_W / 2}
            y={co.cy - PILL_H / 2}
            width={PILL_W}
            height={PILL_H}
            rx={PILL_RX}
            fill={C.pillFill}
            stroke={C.pillStroke}
            strokeWidth={1}
          />
          <foreignObject
            x={co.cx - PILL_W / 2}
            y={co.cy - PILL_H / 2}
            width={PILL_W}
            height={PILL_H}
            style={{ pointerEvents: 'none' }}
          >
            <div style={foCenterRow}>
              <span
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  fontWeight: 500,
                  color: C.pillText,
                  whiteSpace: 'nowrap',
                }}
              >
                {co.label}
              </span>
            </div>
          </foreignObject>
        </g>
      )),
    )}

    {/* Entity ovals */}
    {ENTITIES.map((entity) => (
      <g
        key={`entity-${entity.id}`}
        style={{ cursor: 'pointer' }}
        onClick={() => onEntityClick(entity)}
      >
        <ellipse
          cx={entity.cx}
          cy={entity.cy}
          rx={entity.r * 1.8}
          ry={entity.r}
          fill={C.entityFill}
          stroke={C.entityStroke}
          strokeWidth={1.5}
        />
        <foreignObject
          x={entity.cx - entity.r * 1.5}
          y={entity.cy - entity.r * 0.7}
          width={entity.r * 3}
          height={entity.r * 1.4}
          style={{ pointerEvents: 'none' }}
        >
          <div style={foCenter}>
            <div
              style={{
                fontFamily: FONT,
                fontSize: 18,
                fontWeight: 600,
                color: C.entityText,
                lineHeight: 1.2,
              }}
            >
              {entity.label}
            </div>
            <div
              style={{
                fontFamily: FONT,
                fontSize: 12,
                fontWeight: 400,
                color: C.entitySub,
                lineHeight: 1.3,
                marginTop: 2,
              }}
            >
              {entity.sub}
            </div>
          </div>
        </foreignObject>
      </g>
    ))}

    {/* Mom circle */}
    <circle
      cx={MOM.cx}
      cy={MOM.cy}
      r={MOM.r}
      fill={C.momFill}
      stroke={C.momStroke}
      strokeWidth={2.5}
    />
    <foreignObject
      x={MOM.cx - MOM.r * 0.7}
      y={MOM.cy - MOM.r * 0.7}
      width={MOM.r * 1.4}
      height={MOM.r * 1.4}
      style={{ pointerEvents: 'none' }}
    >
      <div style={foCenterRow}>
        <span
          style={{
            fontFamily: FONT,
            fontSize: 20,
            fontWeight: 600,
            color: C.momText,
          }}
        >
          Mom
        </span>
      </div>
    </foreignObject>
  </svg>
);

// ============================================
// Detail Dialog Component
// ============================================

interface DetailDialogProps {
  title: string;
  subtitle: string;
  stats: { label: string; value: string }[];
  onClose: () => void;
}

const DetailDialog: React.FC<DetailDialogProps> = ({
  title,
  subtitle,
  stats,
  onClose,
}) => (
  <DialogOverlay
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    onClick={onClose}
  >
    <DialogCard
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <DialogClose onClick={onClose}>&times;</DialogClose>
      <DialogTitle>{title}</DialogTitle>
      <DialogSubtitle>{subtitle}</DialogSubtitle>
      {stats.map((s) => (
        <DialogStat key={s.label}>
          <StatLabel>{s.label}</StatLabel>
          <StatValue>{s.value}</StatValue>
        </DialogStat>
      ))}
    </DialogCard>
  </DialogOverlay>
);

// ============================================
// Slide
// ============================================

export const ProblemSlide: React.FC = () => {
  const [dialog, setDialog] = useState<{
    title: string;
    subtitle: string;
    stats: { label: string; value: string }[];
  } | null>(null);

  return (
    <SlideBase>
      <SlideHeader
        title="Problem"
        subtitle="Moms provide $145k worth of invisible labor in their households with antiquated support"
      />

      <BodyRow>
        <DiagramPanel>
          <ProblemDiagram
            onEntityClick={(entity) =>
              setDialog({
                title: entity.label,
                subtitle: entity.detail.subtitle,
                stats: entity.detail.stats,
              })
            }
            onCompanyClick={(co) =>
              setDialog({
                title: co.label,
                subtitle: co.detail.subtitle,
                stats: co.detail.stats,
              })
            }
          />
          <SlideNote style={{ paddingTop: spacing.lg }}>Moms need tools for accomplishing goals not for purchases <br />the gap is planning, budgeting, tracking and communication.</SlideNote>
        </DiagramPanel>

        <VerticalDivider />

        <RowAlertPanel columns={2}>
          <SlideAlertBox headline="Household Tech Is Cobbled Together from Other Purpose Built Apps">
            Moms get a shared Google Calendar, spreadsheet, group text, sticky notes, and thousands of piecemeal apps. In comparison they work at companies spending $4k per employee on integrated SaaS.
          </SlideAlertBox>

          <SlideAlertBox headline="The Cognitive Load is Massive">
            Moms average ~19 household app opens per day across kids, meals, home, cars, pets, and schedules — context-switching each time. A single date night requires 7+ steps across multiple days.
          </SlideAlertBox>

          <SlideAlertBox headline="Money Makes It Worse">
            Every additional service (nanny, cleaner, dog walker, tutor) creates a new management task: vetting, scheduling, communicating, and monitoring. Mom becomes the unpaid COO running a small business.
          </SlideAlertBox>

          <SlideAlertBox headline="You Can&rsquo;t Help What You Can&rsquo;t See">
            Moms carry <DarkInsightEmphasis>73% of the cognitive load</DarkInsightEmphasis>, mostly as context in their heads. There&rsquo;s no &ldquo;hand off&rdquo; button. &ldquo;What do you need me to do?&rdquo; just creates another task.
          </SlideAlertBox>
        </RowAlertPanel>
      </BodyRow>

      <AnimatePresence>
        {dialog && (
          <DetailDialog
            title={dialog.title}
            subtitle={dialog.subtitle}
            stats={dialog.stats}
            onClose={() => setDialog(null)}
          />
        )}
      </AnimatePresence>
    </SlideBase>
  );
};
